import { useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import CountUp from '../components/CountUp';
import ClaimModal from '../components/ClaimModal';
import ClaimTickets from '../components/ClaimTickets';
import { useAuth } from '../context/AuthContext';
import { API_BASE, apiFetch } from '../apiBase';
import { fmtWager, fmtReward, fmtDate, fmtDateTime } from '../lib/wager';

// The milestone tables, the cycle and each player's verified wagers come from
// /api/benefits (src/milestones.js on the backend). This file only holds how
// each platform looks.
const CYCLE = 1209600000; // 14 days — the countdown's fallback until the API answers
const ANCHOR = 1704182400000;
const pad = (n) => String(n).padStart(2, '0');

const REWARD_COLOR = { red: '#ffdad6', gold: '#F6BB42', blue: '#5aa9ff' };
const ACCENT = { red: '#ff544a', gold: '#F6BB42', blue: '#5aa9ff' };

const VISUALS = {
  raingg: {
    logo: 'https://lh3.googleusercontent.com/aida-public/AB6AXuA4LtZO6pTtI2dVOsPbDXL1Ijkkql5lnsXCGbvK8ifVDzX_wzkd2v2OnqyvDRqyhuappMO7IH_dZ1Up4DR7q-l3CSuHZ5zzh2JI6cNnAFJSRzqhAuz561KPxTv1DPC6oVC0KUDY378pa_rdEj0CtJjsh7vW27fMCUGFypOQ8aKpItqGOLEKbgNoYw7aeXcJ6APCr382Z76gnr4jAtofHiHOILvGnw4LG7jLAH0HD4QttvrCqJT8aR9xSL8oy4JEG_DYaw',
    accent: 'red',
  },
  unbox: {
    logo: 'https://lh3.googleusercontent.com/aida-public/AB6AXuBb-aUjtpOKuV-xPjX2t7bukiph91wQCo8uthITRfL4ReHaaRpw7hXvO_cZky1UrQY15YBTDjGDN4A9J3STvFPRCI3yOI7G1vZKfcA0-ozmeSdZEbgDybwMrkpDaayqQd2eHHl__T3NDqmhBFQxkVTyGryRo8_3VxSIPeoEXjPI-xZwOGyzBAct7Sx7GGua9AWBaY8mH8aoBADB2X1zjlSp-ziQyYOFUOGepDNnYpHRpDgh2haWZ75tKQmdvbHf1y8hyA',
    accent: 'red',
  },
  betdice: {
    logo: 'https://lh3.googleusercontent.com/aida-public/AB6AXuApasZeLa_AwQhPJxpWmK2iAyyuwGDArSdv-nJ5oJ9sYWIofvWWzIpIBHdzgyuQ_tTBcAgPm7XNBfJ4pLlf3z2GOMVQrafdgEWSg1pVNWxp9qX1qh27gBgfanNkttreUr5jPiJqkfvzFFtAxAu6A0JxsG13eeRAs_wuwge5eXQ6OzR1H4xFpXXid-JYkWtuP0CbYnmtcsPjeHErucnz2gpNB8SdUXb4loBSnVYnrT78mrVqC1LjxqRqhYB4j4Fo0CMZxw',
    accent: 'gold',
  },
  casesgg: { logo: '/images/casesgg-logo.png', accent: 'blue' },
  winovo: {
    logo: '/images/winovo-logo.png',
    accent: 'gold',
    // Winovo also pays a deposit bonus that scales with the deposit size.
    extra: {
      heading: 'Deposit Bonuses',
      rows: [
        ['$50 – $500', 10],
        ['$500 – $2,500', 15],
        ['$2,500 – $50,000', 20],
      ],
    },
  },
};

const openSettings = () => window.dispatchEvent(new Event('open-profile'));

/** What the claim button for one cycle should offer, from the verified tier and the claim state. */
function claimAction(result, claims) {
  if (!result || result.status !== 'ok' || !result.tier) return null;
  const tier = result.tier;
  if (claims.openTicketId && tier <= claims.openTier) return { kind: 'open', ticketId: claims.openTicketId, tier: claims.openTier };
  if (claims.openTicketId) return { kind: 'upgrade', tier, upgradeOf: claims.openTicketId };
  if (tier <= claims.paidTier) return { kind: 'paid', tier: claims.paidTier };
  if (tier <= claims.rejectedTier) return { kind: 'rejected', tier: claims.rejectedTier };
  return { kind: 'claim', tier, paidReward: claims.paidReward };
}

function ClaimButton({ action, platform, accent, onClaim, onShowTicket }) {
  if (!action) return null;
  const reward = fmtReward(platform.unit, platform.milestones[action.tier - 1].reward);
  if (action.kind === 'paid') {
    return <div className="text-center text-sm font-bold py-2.5" style={{ color: '#5ee08a' }}>✓ Milestone {action.tier} paid</div>;
  }
  if (action.kind === 'rejected') {
    return <div className="text-center text-xs py-2.5 text-[#ff8f86]">Your claim for milestone {action.tier} was rejected — ask on Discord if that's a mistake</div>;
  }
  if (action.kind === 'open') {
    return (
      <button onClick={() => onShowTicket(action.ticketId)} className="w-full rounded-lg py-2.5 text-sm font-bold cursor-pointer border border-[#F6BB4266] text-[#F6BB42] hover:bg-[#F6BB420f]">
        Claim open — ticket #{action.ticketId}
      </button>
    );
  }
  return (
    <button
      onClick={onClaim}
      className="shine-hover w-full rounded-lg py-2.5 text-sm font-black cursor-pointer transition-transform hover:scale-[1.02]"
      style={{ background: ACCENT[accent], color: '#0B0B14' }}
    >
      {action.kind === 'upgrade' ? `Upgrade claim to milestone ${action.tier} — ${reward}` : `Claim milestone ${action.tier} — ${reward}`}
    </button>
  );
}

function YourWager({ platform, status, cycle, accent, onClaim, onShowTicket }) {
  const box = 'rounded-lg bg-black/40 border border-[#24242e] p-4';
  if (!status) return <div className={`${box} text-sm text-[#7d7d8c]`}>Checking your wagers…</div>;
  if (!status.linked) {
    return (
      <div className={box}>
        <p className="text-sm text-[#c9c9d6] mb-3">Link your {platform.accountLabel} to track your wagers here.</p>
        <button onClick={openSettings} className="w-full rounded-lg py-2 text-sm font-bold cursor-pointer border border-[#353535] text-white hover:border-[#ff544a80]">
          Open Settings
        </button>
      </div>
    );
  }
  const cur = status.current;
  if (!cur || cur.status !== 'ok') {
    return <div className={`${box} text-sm text-[#7d7d8c]`}>Wager tracking for {platform.name} is temporarily unavailable.</div>;
  }

  const ms = platform.milestones;
  const next = ms[cur.tier]; // tier is 1-based, so this is the next one up
  const from = cur.tier ? ms[cur.tier - 1].threshold : 0;
  const pct = next ? Math.min(1, Math.max(0, (cur.wagered - from) / (next.threshold - from))) : 1;
  const prev = status.previous;
  const prevAction = cycle.previousClaimable ? claimAction(prev, status.claims.previous) : null;

  return (
    <div className="flex flex-col gap-3">
      <div className={box}>
        <div className="flex items-baseline justify-between gap-2">
          <span className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[#888899]">Your wager this cycle</span>
          <span className="text-[10px] text-[#5d5d6c] truncate max-w-[45%]" title={status.account}>{status.account}</span>
        </div>
        <div className="font-mono-brand text-2xl font-black text-white mt-1">
          <CountUp value={cur.wagered} formatter={(n) => fmtWager(platform.unit, n)} />
        </div>
        <div className="h-2 rounded-full bg-[#181820] overflow-hidden mt-3">
          <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct * 100}%`, background: ACCENT[accent] }} />
        </div>
        <div className="text-[11px] text-[#888899] mt-1.5">
          {next
            ? `${fmtWager(platform.unit, next.threshold - cur.wagered)} to milestone ${next.tier} (${fmtReward(platform.unit, next.reward)})`
            : 'Every milestone reached this cycle'}
        </div>
        {!cur.found && (
          <div className="text-[11px] text-[#7d7d8c] mt-2">
            No wagers found for this account under our code yet this cycle{cur.topOnly ? ' (Rain.gg only shares its top 50 players)' : ''}. Check the ID in Settings if that's wrong.
          </div>
        )}
        {cur.countingSince && (
          <div className="text-[11px] text-[#7d7d8c] mt-2">Counted from {fmtDateTime(cur.countingSince)}, when tracking started.</div>
        )}
      </div>
      <ClaimButton action={claimAction(cur, status.claims.current)} platform={platform} accent={accent} onClaim={() => onClaim('current', claimAction(cur, status.claims.current), cur.wagered)} onShowTicket={onShowTicket} />
      {prevAction && (
        <div className="rounded-lg border border-[#F6BB4240] bg-[#F6BB420a] p-3 flex flex-col gap-2">
          <div className="text-[12px] text-[#e6e6ea]">
            Last cycle ({fmtDate(cycle.previousStart)} – {fmtDate(cycle.start)}): {fmtWager(platform.unit, prev.wagered)} — milestone {prev.tier}.
            {prevAction.kind === 'claim' || prevAction.kind === 'upgrade' ? ` Claim before ${fmtDateTime(cycle.graceEndsAt)}.` : ''}
          </div>
          <ClaimButton action={prevAction} platform={platform} accent={accent} onClaim={() => onClaim('previous', prevAction, prev.wagered)} onShowTicket={onShowTicket} />
        </div>
      )}
    </div>
  );
}

export default function Benefits() {
  const { user, login } = useAuth();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [openTicket, setOpenTicket] = useState(null);
  const [claiming, setClaiming] = useState(null); // { platform, claim }
  const [now, setNow] = useState(() => Date.now());
  const reloadTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch('/api/benefits');
      if (!res.ok) throw new Error();
      setData(await res.json());
      setLoadError(false);
    } catch {
      setLoadError(true);
    }
    if (user) {
      // awaited, so a caller can scroll to a ticket that was just opened
      await apiFetch('/api/benefits/claims').then((r) => (r.ok ? r.json() : [])).then(setTickets).catch(() => {});
    } else {
      setTickets([]);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  // A staff reply or status change anywhere pings every page; only our own data is re-read.
  useEffect(() => {
    const socket = io(API_BASE || undefined, { path: '/socket.io' });
    const soon = () => {
      clearTimeout(reloadTimer.current);
      reloadTimer.current = setTimeout(load, 400);
    };
    socket.on('claims:update', soon);
    window.addEventListener('profile:saved', soon);
    return () => {
      socket.disconnect();
      window.removeEventListener('profile:saved', soon);
      clearTimeout(reloadTimer.current);
    };
  }, [load]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const cycleEnd = data ? data.cycle.end : ANCHOR + (Math.floor((now - ANCHOR) / CYCLE) + 1) * CYCLE;
  // a new cycle started while the page was open: fetch the new one
  useEffect(() => {
    if (data && now >= data.cycle.end) load();
  }, [now, data, load]);
  const diff = Math.max(0, cycleEnd - now);
  const Dd = Math.floor(diff / 86400000);
  const Hh = Math.floor((diff % 86400000) / 3600000);
  const Mm = Math.floor((diff % 3600000) / 60000);
  const Ss = Math.floor((diff % 60000) / 1000);

  const me = data?.me?.platforms;
  const trackedLinked = data && me ? data.platforms.some((p) => p.tracked && me[p.id]?.linked) : true;

  function showTicket(id) {
    setOpenTicket(id);
    setTimeout(() => document.getElementById(`ticket-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
  }

  function startClaim(platform, which, action, wagered) {
    const c = data.cycle;
    setClaiming({
      platform,
      claim: {
        platform: platform.id,
        cycle: which,
        tier: action.tier,
        wagered,
        upgradeOf: action.kind === 'upgrade' ? action.upgradeOf : null,
        paidReward: action.paidReward || 0,
        cycleStart: which === 'previous' ? c.previousStart : c.start,
        cycleEnd: which === 'previous' ? c.start : c.end,
      },
    });
  }

  return (
    <main className="max-w-[1200px] mx-auto px-6 py-12 relative z-10">
      <div className="scroll-reveal text-center mb-16 space-y-4">
        <h1 className="font-display-lg text-3xl sm:text-4xl md:text-5xl text-white font-extrabold">
          Unlock Exclusive Benefits with <span className="text-[#ff544a]">FREEMAN</span>
        </h1>
        <p className="font-title-md text-[#888899] max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">
          Hit personal wager targets during each period and claim extra prizes. Milestones reset with every 2 Week
          cycle; only wagers in the current timeframe count.
        </p>
      </div>

      <div className="scroll-reveal timer-card rounded-xl p-8 max-w-2xl mx-auto mb-10 text-center relative overflow-hidden">
        <div className="timer-glow" />
        <div className="relative z-10 flex flex-col items-center gap-2">
          <span className="font-mono-brand text-xs text-[#F6BB42] uppercase tracking-[0.3em] font-bold">
            Next Milestone Reset In
          </span>
          <div style={{ height: 1, width: 64, background: 'linear-gradient(90deg,transparent,rgba(255,84,74,.6),transparent)' }} />
        </div>
        <div className="timer-wrap relative z-10">
          {[
            ['Days', Dd],
            ['Hours', Hh],
            ['Min', Mm],
            ['Sec', Ss],
          ].flatMap(([lbl, val], idx, arr) => {
            const box = (
              <div className="timer-box" key={lbl}>
                <div className="timer-digit">{pad(val)}</div>
                <div className="timer-label">{lbl}</div>
              </div>
            );
            return idx < arr.length - 1 ? [box, <div className="timer-colon" key={lbl + '-sep'}>:</div>] : [box];
          })}
        </div>
      </div>

      {!user && (
        <div className="scroll-reveal glass-panel neon-border rounded-xl p-5 max-w-2xl mx-auto mb-12 flex flex-col sm:flex-row items-center gap-4 justify-between">
          <p className="text-sm text-[#c9c9d6]">Sign in to see your wagers on each site and claim the milestones you reach.</p>
          <button onClick={() => login()} className="rounded-lg px-5 py-2.5 text-sm font-black cursor-pointer flex-shrink-0" style={{ background: '#53FC18', color: '#0B0B14' }}>
            Sign in with Kick
          </button>
        </div>
      )}
      {user && data && !trackedLinked && (
        <div className="scroll-reveal glass-panel neon-border rounded-xl p-5 max-w-2xl mx-auto mb-12 flex flex-col sm:flex-row items-center gap-4 justify-between">
          <p className="text-sm text-[#c9c9d6]">Link your Cases.gg, Winovo or Rain.gg account in Settings and your wagers show up here.</p>
          <button onClick={openSettings} className="btn-primary rounded-lg px-5 py-2.5 text-sm flex-shrink-0">Open Settings</button>
        </div>
      )}

      {loadError && !data && (
        <p className="text-center text-sm text-[#ff544a] mb-12">Couldn't load the milestones — refresh the page to try again.</p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
        {data?.platforms.map((p, i) => {
          const v = VISUALS[p.id] || { accent: 'red' };
          // signed in but the server couldn't build our status: say so instead of "checking…" forever
          const s = me ? me[p.id] : { linked: true, current: { status: 'unavailable' } };
          const reached = s?.current?.status === 'ok' ? s.current.tier : 0;
          return (
            <div
              key={p.id}
              className={`scroll-reveal glass-panel benefit-card benefit-card-${v.accent} rounded-xl overflow-hidden flex flex-col group`}
              style={{ transitionDelay: `${i * 90}ms` }}
            >
              <div className="p-6 bg-black flex justify-center items-center h-32 border-b border-[#353535] relative">
                <div
                  className="benefit-logo-glow absolute inset-0"
                  style={{ background: v.accent === 'red' ? 'radial-gradient(circle,rgba(255,84,74,.5),transparent 70%)' : 'radial-gradient(circle,rgba(246,187,66,.5),transparent 70%)' }}
                />
                {v.logo ? <img alt={p.name} className="h-16 object-contain relative z-10" src={v.logo} /> : <span className="text-white font-black text-2xl relative z-10">{p.name}</span>}
              </div>
              <div className="p-6 flex-grow space-y-4">
                {user && p.tracked && (
                  <YourWager platform={p} status={s} cycle={data.cycle} accent={v.accent} onClaim={(which, action, wagered) => startClaim(p, which, action, wagered)} onShowTicket={showTicket} />
                )}
                {user && !p.tracked && (
                  <div className="rounded-lg bg-black/40 border border-[#24242e] p-4 text-[12px] text-[#888899]">
                    {p.name} wagers can't be tracked automatically yet — claim these milestones through our Discord.
                  </div>
                )}
                <h3 className="font-title-md text-lg text-white font-bold mb-4">Milestone Rewards</h3>
                <div className="space-y-3">
                  {p.milestones.map((m) => {
                    const done = m.tier <= reached;
                    return (
                      <div key={m.tier} className="flex items-center gap-3 border-b border-[#353535] pb-2" style={done ? { borderColor: `${ACCENT[v.accent]}66` } : undefined}>
                        <span
                          className={`tier-badge tier-badge-${v.accent}`}
                          style={done ? { background: ACCENT[v.accent], color: '#0B0B14', borderColor: ACCENT[v.accent] } : undefined}
                        >
                          {done ? '✓' : m.tier}
                        </span>
                        <span className="benefit-row font-mono-brand text-xs" style={{ color: done ? '#e6e6ea' : '#888899' }}>
                          {m.threshold.toLocaleString()} WAGERED
                        </span>
                        <span className="reward-pill font-mono-brand text-sm font-black" style={{ color: REWARD_COLOR[v.accent] || '#F6BB42' }}>
                          <CountUp value={m.reward} formatter={(n) => fmtReward(p.unit, Math.round(n))} />
                        </span>
                      </div>
                    );
                  })}
                </div>
                {v.extra && (
                  <div className="pt-4 space-y-3">
                    <h3 className="font-title-md text-lg text-white font-bold mb-4">{v.extra.heading}</h3>
                    {v.extra.rows.map(([range, pct], ri) => (
                      <div key={range} className="flex items-center gap-3 border-b border-[#353535] pb-2">
                        <span className={`tier-badge tier-badge-${v.accent}`}>{ri + 1}</span>
                        <span className="benefit-row font-mono-brand text-xs text-[#888899]">{range} DEPOSIT</span>
                        <span className="reward-pill font-mono-brand text-sm font-black" style={{ color: REWARD_COLOR[v.accent] || '#F6BB42' }}>
                          <CountUp value={pct} formatter={(n) => `${Math.round(n)}%`} />
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <ClaimTickets tickets={tickets} openId={openTicket} setOpenId={setOpenTicket} onChange={() => load()} />

      <div className="scroll-reveal glass-panel p-8 rounded-xl neon-border text-center flex flex-col items-center justify-center max-w-3xl mx-auto space-y-6">
        <p className="font-title-md text-lg text-white font-bold">
          Cases.gg, Winovo and Rain.gg milestones are claimed right here. For BetDice and Unbox, open a ticket in our Discord.
        </p>
        <a
          href="https://discord.gg/ubnZnbsvCv"
          target="_blank"
          rel="noopener noreferrer"
          className="shine-hover inline-flex items-center justify-center rounded-lg transition-transform hover:scale-105 active:scale-95"
        >
          <img
            src="https://lh3.googleusercontent.com/aida-public/AB6AXuDWcNcwNpBYQDQN7Qde2Rl4FtaRR38Qz4xCQX4Trh8UL3j4jbjrvjt_-PveQRU4sTjz4YX4bEX9evltZTadtWVxdi_nqLtw1K8qnie-SFfBb45zMwO1tFFcmt-BvyWoyz7sB8jCQU0c2wsnZrIbKXbdc8IWdKlB8Ha0NnZDojVHwH3BkFfhzUac8gcoSadmW3U63MaxwXOnMonbFYomvyNPBTr-MHMuzbjffCYHndb-WVOCpnFamr7PRQDrWiR7ax75TQ"
            alt="Discord"
            className="h-12 object-contain"
          />
        </a>
      </div>

      {claiming && (
        <ClaimModal
          claim={claiming.claim}
          platform={claiming.platform}
          onClose={() => setClaiming(null)}
          onDone={(ticket) => {
            setClaiming(null);
            load().then(() => showTicket(ticket.id));
          }}
        />
      )}
    </main>
  );
}
